import { useState, useCallback, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import Header from "./components/Header";
import SearchForm from "./components/SearchForm";
import QuotaNotice from "./components/QuotaNotice";
import ProgressTracker from "./components/ProgressTracker";
import ResultsDashboard from "./components/ResultsDashboard";
import { useAuth } from "./context/AuthContext";
import { useResearchQuota } from "./context/QuotaContext";
import { API_URL, HTTP_STATUS_TOO_MANY_REQUESTS, HTTP_STATUS_UNAUTHORIZED } from "./lib/api";
import {
  CategoriesState,
  CategoryKey,
  CATEGORY_ORDER,
  QuotaSnapshot,
  initialCategoriesState,
} from "./types";

const SESSION_EXPIRED_MESSAGE = "Sessão expirada. Entre novamente.";

export default function App() {
  const { logout } = useAuth();
  const { quota, refresh: refreshQuota, applySnapshot } = useResearchQuota();
  const [categories, setCategories] = useState<CategoriesState>(
    initialCategoriesState()
  );
  const [isResearching, setIsResearching] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [hasResults, setHasResults] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const handleSearch = useCallback(
    async (name: string, location: string) => {
      // Reset state
      setCategories(initialCategoriesState());
      setIsResearching(true);
      setCompanyName(name);
      setHasResults(false);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch(`${API_URL}/api/underwrite/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_name: name,
            location: location || undefined,
          }),
          credentials: "include",
          signal: controller.signal,
        });

        if (response.status === HTTP_STATUS_UNAUTHORIZED) {
          await logout();
          setCategories((prev) => {
            const next = { ...prev };
            for (const key of CATEGORY_ORDER) {
              next[key] = {
                ...prev[key],
                status: "error" as const,
                progressMessage: SESSION_EXPIRED_MESSAGE,
              };
            }
            return next;
          });
          return;
        }

        if (response.status === HTTP_STATUS_TOO_MANY_REQUESTS) {
          const body = await response.json().catch(() => undefined);
          const quotaSnapshot = body?.quota as QuotaSnapshot | undefined;
          if (quotaSnapshot) {
            applySnapshot(quotaSnapshot);
          }
          return;
        }

        if (!response.ok) {
          throw new Error(response.statusText || "Request failed");
        }
        if (!response.body) {
          throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const event = JSON.parse(line.slice(6));
                handleEvent(event);
              } catch {
                // skip malformed lines
              }
            }
          }
        }
      } catch (err: any) {
        if (err.name !== "AbortError") {
          console.error("Stream error:", err);
        }
      } finally {
        setIsResearching(false);
        refreshQuota().catch(() => undefined);
      }
    },
    [logout, refreshQuota, applySnapshot]
  );

  const handleEvent = useCallback((event: any) => {
    const cat = event.category as CategoryKey | undefined;

    switch (event.type) {
      case "start":
        // mark all categories as pending (already default)
        break;

      case "progress":
        if (cat) {
          setCategories((prev) => ({
            ...prev,
            [cat]: {
              ...prev[cat],
              status: "in_progress" as const,
              progressMessage: event.message || "",
            },
          }));
        }
        break;

      case "sources_found":
        if (cat) {
          setCategories((prev) => ({
            ...prev,
            [cat]: {
              ...prev[cat],
              sources: [
                ...prev[cat].sources,
                ...(event.sources || []),
              ],
            },
          }));
        }
        break;

      case "category_complete":
        if (cat) {
          setCategories((prev) => ({
            ...prev,
            [cat]: {
              status: "completed" as const,
              data: event.data || {},
              sources: event.sources || prev[cat].sources,
              progressMessage: "",
            },
          }));
          setHasResults(true);
        }
        break;

      case "error": {
        const message = event.message || "Ocorreu um erro";
        if (cat) {
          setCategories((prev) => ({
            ...prev,
            [cat]: {
              ...prev[cat],
              status: "error" as const,
              progressMessage: message,
            },
          }));
        } else {
          // Erro global (ex.: chave da API ausente) — sinaliza em todas as categorias
          setCategories((prev) => {
            const next = { ...prev };
            for (const key of CATEGORY_ORDER) {
              next[key] = { ...prev[key], status: "error" as const, progressMessage: message };
            }
            return next;
          });
        }
        break;
      }

      case "complete":
        // Final event — all done
        break;
    }
  }, []);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    setIsResearching(false);
  }, []);

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Background image layer */}
      <div
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/tavily_landscapes_edited_11.webp)',
          opacity: 0.7,
          willChange: 'transform',
          transform: 'translateZ(0)',
        }}
      />
      {/* Top gradient fade */}
      <div
        className="fixed inset-x-0 top-0 z-0 h-48 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, var(--color-background), transparent)',
          transform: 'translateZ(0)',
        }}
      />
      {/* Bottom gradient fade */}
      <div
        className="fixed inset-x-0 bottom-0 z-0 h-48 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, var(--color-background), transparent)',
          transform: 'translateZ(0)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <Header />

        <div className="mt-10 space-y-4">
          <QuotaNotice variant="notice" />
          <SearchForm
            onSearch={handleSearch}
            isLoading={isResearching}
            onCancel={handleCancel}
            disabled={Boolean(quota?.blocked_by) && !quota?.exempt}
          />
        </div>

        <AnimatePresence mode="wait">
          {isResearching && (
            <ProgressTracker
              key="progress"
              categories={categories}
              companyName={companyName}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {hasResults && (
            <ResultsDashboard
              key="results"
              categories={categories}
              companyName={companyName}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
